/**
 * LiveSeoEditorPanel — the Surfer/Clearscope-style side-by-side live editor.
 *
 * THIN by design: every number, chip, card, and preview comes from the pure
 * view-model in lib/content/editorViewModel (which wraps lib/content/seoScore),
 * and every button dispatches one of its tested transitions:
 *   - fix cards call applyFix → new state → rescore, with a visible
 *     before/after delta toast (diffScore),
 *   - Undo restores the previous snapshot (timeMachine's restore pattern,
 *     held in view memory),
 *   - SERP + AEO previews are collapsible render-only artifacts.
 *
 * Scoring is synchronous and sub-millisecond, so the panel re-renders on
 * every keystroke — no spinner, no debounce, no dead buttons.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  Bot,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  ListTree,
  Loader2,
  PenLine,
  Search,
  Sparkles,
  Undo2,
  Wand2,
  Wrench,
  X,
} from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import {
  applyFix,
  buildAeoPreview,
  buildPanelViewModel,
  buildSerpPreview,
  diffScore,
  parseOutline,
  type EditorState,
} from '../lib/content/editorViewModel';
import type { KeywordTarget } from '../lib/content/seoScore';
import {
  extractSection,
  replaceSection,
  runRewrite,
  toApprovalAction,
  type RewriteResult,
  type WorkerPort,
} from '../lib/content/rewriteMission';
import { generateBlogFromTopic } from '../lib/content/blogFromTopic';
import * as argoWorker from '../lib/agent/argoWorker';

const stateColor: Record<'red' | 'yellow' | 'green', string> = {
  red: C.orangeWarm,
  yellow: C.orange,
  green: C.green,
};

const inputStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 10,
  padding: '9px 11px',
  color: C.text,
  fontSize: 13,
  outline: 'none',
  fontFamily: 'inherit',
};

function buildTargets(primary: string, secondaries: string): KeywordTarget[] {
  const out: KeywordTarget[] = [];
  const p = primary.trim();
  if (p.length > 0) out.push({ term: p, kind: 'primary', minUses: 3, maxUses: 20 });
  for (const s of secondaries.split(',').map((x) => x.trim()).filter((x) => x.length > 0)) {
    out.push({ term: s, kind: 'secondary', minUses: 1, maxUses: 10 });
  }
  return out;
}

function Collapsible({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="glass-btn"
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'transparent',
          border: 'none',
          padding: '10px 12px',
          cursor: 'pointer',
          color: C.text,
        }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {icon}
        <span className="t-section-title" style={{ fontSize: 12 }}>{title}</span>
      </button>
      {open && <div style={{ padding: '0 12px 12px', display: 'grid', gap: 10 }}>{children}</div>}
    </div>
  );
}

function JsonLdBlock({ label, json }: { label: string; json: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="t-meta" style={{ color: C.textMuted }}>{label}</span>
        <button
          className="glass-btn"
          onClick={() => {
            navigator.clipboard?.writeText(json).then(() => setCopied(true)).catch(() => undefined);
          }}
          style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, padding: '3px 9px', cursor: 'pointer' }}
        >
          <Copy size={11} /> {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre
        style={{
          margin: 0,
          padding: 10,
          maxHeight: 180,
          overflow: 'auto',
          fontSize: 11,
          lineHeight: 1.5,
          color: C.textMuted,
          background: 'rgba(0,0,0,0.3)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 10,
          whiteSpace: 'pre-wrap',
        }}
      >
        {json}
      </pre>
    </div>
  );
}

export function LiveSeoEditorPanel() {
  const [title, setTitle] = useState('');
  const [meta, setMeta] = useState('');
  const [markdown, setMarkdown] = useState('');
  const [primaryKw, setPrimaryKw] = useState('');
  const [secondaryKw, setSecondaryKw] = useState('');
  // Snapshot stack for Undo — the timeMachine restore pattern, in memory.
  const [history, setHistory] = useState<EditorState[]>([]);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  const editorState = useMemo<EditorState>(
    () => ({ title, meta, markdown, targets: buildTargets(primaryKw, secondaryKw) }),
    [title, meta, markdown, primaryKw, secondaryKw],
  );

  // Pure + synchronous: recomputed on every keystroke, no spinner needed.
  const vm = useMemo(() => buildPanelViewModel(editorState), [editorState]);
  const serp = useMemo(() => buildSerpPreview(editorState), [editorState]);
  const aeo = useMemo(() => buildAeoPreview(editorState), [editorState]);
  const outline = useMemo(() => parseOutline(markdown), [markdown]);

  const restore = (s: EditorState) => {
    setTitle(s.title);
    setMeta(s.meta);
    setMarkdown(s.markdown);
  };

  const handleFix = (fixId: string) => {
    const next = applyFix(editorState, fixId);
    const d = diffScore(editorState, next);
    setHistory((h) => [...h, editorState]);
    restore(next);
    setToast(`${d.delta >= 0 ? '+' : ''}${d.delta} pts · ${d.from} → ${d.to}`);
  };

  const handleUndo = () => {
    const prev = history[history.length - 1];
    if (!prev) return;
    const d = diffScore(editorState, prev);
    setHistory((h) => h.slice(0, -1));
    restore(prev);
    setToast(`Restored previous version · ${d.from} → ${d.to}`);
  };

  // ------ Governed agentic section rewrite (rewriteMission) ---------------
  // Real worker when configured; deterministic fallback otherwise. The
  // proposal NEVER auto-applies — it renders as a diffed approval card and
  // becomes state only on Approve (with an undo snapshot = reversal).
  const [missionBusy, setMissionBusy] = useState<string | null>(null);
  const [missionError, setMissionError] = useState<string | null>(null);
  const [mission, setMission] = useState<{ heading: string; result: RewriteResult } | null>(
    null,
  );

  const workerPort = useMemo<WorkerPort>(
    () => ({
      async rewrite(prompt: string) {
        // Honest seam: no configured worker → null → deterministic fallback.
        if (!argoWorker.isConfigured()) return null;
        const out = await argoWorker.runAgent(
          {
            id: 'seo-section-rewriter',
            name: 'SEO section rewriter',
            template: 'content-rewrite',
            persona: 'Senior SEO editor — answer-first, on-brand, no invented facts',
          },
          { prompt },
        );
        return out.text.trim().length > 0 ? { text: out.text } : null;
      },
    }),
    [],
  );

  // ------ Generate blog from topic (blogFromTopic) ------------------------
  // Real Claude draft when the worker is configured; deterministic AEO-shaped
  // outline otherwise — the pill under the button says which one, up front.
  const [blogTopic, setBlogTopic] = useState('');
  const [blogBusy, setBlogBusy] = useState(false);
  const [blogNote, setBlogNote] = useState<string | null>(null);
  const workerConfigured = argoWorker.isConfigured();

  const blogWorkerPort = useMemo<WorkerPort>(
    () => ({
      async rewrite(prompt: string) {
        // Honest seam: no configured worker → null → deterministic fallback.
        if (!argoWorker.isConfigured()) return null;
        const out = await argoWorker.runAgent(
          {
            id: 'blog-topic-writer',
            name: 'Blog writer',
            template: 'content-generate',
            persona:
              'Senior SEO/AEO blog writer — answer-first, question-shaped H2s, no invented facts',
          },
          { prompt },
        );
        return out.text.trim().length > 0 ? { text: out.text } : null;
      },
    }),
    [],
  );

  const handleGenerateBlog = async () => {
    const topic = blogTopic.trim();
    if (topic.length === 0 || blogBusy) return;
    setBlogBusy(true);
    setBlogNote(null);
    try {
      // generateBlogFromTopic is total (never rejects): worker draft when
      // configured + parseable, deterministic AEO outline otherwise.
      const result = await generateBlogFromTopic(
        { topic, targets: editorState.targets, brandFacts: editorState.brandFacts },
        blogWorkerPort,
      );
      setHistory((h) => [...h, editorState]); // undoable, like every fix
      restore({ ...editorState, ...result.draft });
      // Seed the primary keyword from the topic so coverage chips + the
      // keyword dimension score the same thing the draft was built around.
      if (primaryKw.trim().length === 0) setPrimaryKw(topic.toLowerCase());
      setBlogNote(result.note);
      setToast(
        `${result.source === 'worker' ? 'Claude draft' : 'Deterministic outline'} · ${result.score.grade} · ${result.score.total}/100 · AEO ${result.aeo.score}/100 · undoable`,
      );
    } catch {
      // Belt-and-braces: the module is total, but the button must never strand.
      setBlogNote('Generation failed unexpectedly — the draft was not touched.');
    } finally {
      setBlogBusy(false);
    }
  };

  const handleOptimize = async (heading: string) => {
    const sectionMarkdown = extractSection(markdown, heading);
    if (!sectionMarkdown) {
      setMissionError(`Could not locate the “${heading}” section in the draft.`);
      return;
    }
    setMissionBusy(heading);
    setMissionError(null);
    setMission(null);
    try {
      // runRewrite never rejects on worker failure (deterministic fallback),
      // but guard anyway so the button can never strand in a busy state.
      const result = await runRewrite(
        {
          draftId: 'live-editor',
          sectionHeading: heading,
          sectionMarkdown,
          targets: editorState.targets,
          brandFacts: editorState.brandFacts,
        },
        workerPort,
      );
      setMission({ heading, result });
    } catch {
      setMissionError('Section rewrite failed unexpectedly — nothing was changed.');
    } finally {
      setMissionBusy(null);
    }
  };

  const handleApproveMission = () => {
    if (!mission || !mission.result.contractMet) return;
    const action = toApprovalAction(mission.result); // reversible, sealed
    setHistory((h) => [...h, editorState]); // the undo snapshot IS the reversal
    setMarkdown(replaceSection(markdown, mission.heading, action.diff.proposed));
    setToast(
      `Governed rewrite applied · ${mission.result.scoreBefore} → ${mission.result.scoreAfter} · ${action.provenance.source} · ${action.provenance.promptHash} · undoable`,
    );
    setMission(null);
  };

  const handleRejectMission = () => {
    setMission(null);
    setToast('Rewrite proposal dismissed — draft untouched.');
  };

  const hasContent = title.trim().length > 0 || markdown.trim().length > 0;

  return (
    <GlassPanel style={{ padding: 16, display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <PenLine size={16} style={{ color: C.green }} />
        <span className="t-section-title" style={{ color: C.text }}>
          Live SEO editor
        </span>
        <span className="t-meta" style={{ color: C.textDim }}>
          topic → draft · score-as-you-type · SERP + AEO preview · one-click fixes
        </span>
        {toast && (
          <span
            className="glass-pill"
            role="status"
            style={{ fontSize: 11, padding: '3px 10px', color: C.green, background: `${C.green}22` }}
          >
            {toast}
          </span>
        )}
        <span
          className="glass-pill"
          title="Blended score across all 8 dimensions"
          style={{
            marginLeft: 'auto',
            fontSize: 12,
            padding: '4px 12px',
            fontWeight: 700,
            color: hasContent ? stateColor[vm.total >= 80 ? 'green' : vm.total >= 50 ? 'yellow' : 'red'] : C.textDim,
          }}
        >
          {hasContent ? `${vm.grade} · ${vm.total}/100` : 'start typing to score'}
        </span>
        <button
          className="glass-btn"
          onClick={handleUndo}
          disabled={history.length === 0}
          title={history.length === 0 ? 'Nothing to undo yet' : 'Restore the previous version'}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 12,
            padding: '4px 10px',
            opacity: history.length === 0 ? 0.4 : 1,
            cursor: history.length === 0 ? 'default' : 'pointer',
          }}
        >
          <Undo2 size={12} /> Undo ({history.length})
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.35fr) minmax(0, 1fr)',
          gap: 14,
          alignItems: 'start',
        }}
      >
        {/* ---------------- Editor (left) ---------------- */}
        <div style={{ display: 'grid', gap: 10 }}>
          {/* Generate blog from topic — worker when configured, honest
              deterministic AEO outline otherwise. Fills the fields below,
              which then score live like any hand-typed draft. */}
          <div
            style={{
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 12,
              padding: 10,
              display: 'grid',
              gap: 8,
              background: 'rgba(255,255,255,0.02)',
            }}
          >
            <label className="t-meta" style={{ color: C.textMuted, display: 'flex', alignItems: 'center', gap: 5 }}>
              <Wand2 size={12} style={{ color: C.magentaSoft }} /> Generate blog from topic
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                value={blogTopic}
                onChange={(e) => setBlogTopic(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleGenerateBlog();
                }}
                placeholder="e.g. cutting ticket triage time with agents"
                aria-label="Blog topic"
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                className="glass-btn glass-btn--primary"
                onClick={() => void handleGenerateBlog()}
                disabled={blogBusy || blogTopic.trim().length === 0}
                title={
                  workerConfigured
                    ? 'Draft the full post with the Claude agent-worker, then score it live'
                    : 'No agent-worker configured — generates a deterministic AEO-shaped outline locally'
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  padding: '8px 14px',
                  whiteSpace: 'nowrap',
                  opacity: blogBusy || blogTopic.trim().length === 0 ? 0.5 : 1,
                  cursor: blogBusy || blogTopic.trim().length === 0 ? 'default' : 'pointer',
                }}
              >
                {blogBusy ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Sparkles size={13} />
                )}
                {blogBusy ? 'Generating…' : 'Generate'}
              </button>
            </div>
            <span className="t-meta" style={{ fontSize: 10.5, color: blogNote ? C.textMuted : C.textDim }}>
              {blogNote ??
                (workerConfigured
                  ? 'Claude agent-worker connected — drafts the full post, then the editor scores it live. Undoable.'
                  : 'No agent-worker configured (VITE_AGENT_API_URL) — generates a deterministic AEO-shaped outline you refine with the fix cards. Undoable.')}
            </span>
          </div>

          <div style={{ display: 'grid', gap: 4 }}>
            <label className="t-meta" style={{ color: C.textMuted }}>
              Title tag <span style={{ color: C.textDim }}>({title.trim().length} chars)</span>
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="SERP title — aim for 30–60 characters"
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'grid', gap: 4 }}>
            <label className="t-meta" style={{ color: C.textMuted }}>
              Meta description <span style={{ color: C.textDim }}>({meta.trim().length} chars)</span>
            </label>
            <textarea
              value={meta}
              onChange={(e) => setMeta(e.target.value)}
              placeholder="120–160 character summary that earns the click"
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div style={{ display: 'grid', gap: 4 }}>
              <label className="t-meta" style={{ color: C.textMuted }}>Primary keyword</label>
              <input
                value={primaryKw}
                onChange={(e) => setPrimaryKw(e.target.value)}
                placeholder="e.g. glass dashboards"
                style={inputStyle}
              />
            </div>
            <div style={{ display: 'grid', gap: 4 }}>
              <label className="t-meta" style={{ color: C.textMuted }}>Secondary keywords (comma-separated)</label>
              <input
                value={secondaryKw}
                onChange={(e) => setSecondaryKw(e.target.value)}
                placeholder="e.g. analytics, reporting"
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gap: 4 }}>
            <label className="t-meta" style={{ color: C.textMuted }}>
              Body (markdown) —{' '}
              <span style={{ color: C.textDim }}>
                {vm.wordCount} words · {vm.charCount} chars
              </span>
            </label>
            <textarea
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
              placeholder={'# H1 headline\n\n## What is …?\n\nLead with a direct answer, then expand.'}
              rows={14}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'ui-monospace, monospace', fontSize: 12.5, lineHeight: 1.6 }}
            />
          </div>

          {/* Outline from the same heading parse the scorer uses. */}
          <div style={{ display: 'grid', gap: 4 }}>
            <span className="t-meta" style={{ color: C.textMuted, display: 'flex', alignItems: 'center', gap: 5 }}>
              <ListTree size={12} /> Outline
            </span>
            {outline.length === 0 ? (
              <span className="t-meta" style={{ color: C.textDim }}>
                No headings yet — add an H1 and question-shaped H2s.
              </span>
            ) : (
              <div style={{ display: 'grid', gap: 2 }}>
                {outline.map((h) => (
                  <span
                    key={`${h.line}-${h.text}`}
                    className="t-meta"
                    style={{
                      color: h.level === 1 ? C.text : C.textMuted,
                      paddingLeft: (h.level - 1) * 12,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    H{h.level} · {h.text}
                    {h.level === 2 && (
                      <button
                        className="glass-btn"
                        onClick={() => handleOptimize(h.text)}
                        disabled={missionBusy !== null}
                        title="Agentic rewrite through the governed approval path — diffed, gated, reversible"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          fontSize: 10.5,
                          padding: '2px 8px',
                          cursor: missionBusy !== null ? 'default' : 'pointer',
                          opacity: missionBusy !== null && missionBusy !== h.text ? 0.4 : 1,
                          color: C.magentaSoft,
                        }}
                      >
                        <Bot size={11} />
                        {missionBusy === h.text ? 'Optimizing…' : 'Optimize this section'}
                      </button>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>

          {missionError && (
            <span className="t-meta" role="alert" style={{ color: C.orangeWarm }}>
              {missionError}
            </span>
          )}

          {/* Governed rewrite proposal — the approval-queue affordance. The
              diff renders BEFORE anything is applied; Approve is gated on the
              outcome contract + fidelity, applies reversibly (undo snapshot),
              and stamps the provenance seal into the toast. Never a silent save. */}
          {mission && (
            <GlassPanel
              style={{
                padding: 12,
                display: 'grid',
                gap: 8,
                border: `1px solid ${mission.result.contractMet ? C.green : C.orangeWarm}44`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Bot size={13} style={{ color: C.magentaSoft }} />
                <span className="t-section-title" style={{ fontSize: 12, color: C.text }}>
                  Rewrite proposal · {mission.heading}
                </span>
                <span className="glass-pill" style={{ fontSize: 10.5, padding: '2px 8px', color: C.textMuted }}>
                  {mission.result.source === 'worker' ? 'Argo worker' : 'deterministic fallback'}
                </span>
                <span
                  className="glass-pill"
                  style={{
                    fontSize: 10.5,
                    padding: '2px 8px',
                    color: mission.result.scoreAfter >= mission.result.scoreBefore ? C.green : C.orangeWarm,
                  }}
                >
                  score {mission.result.scoreBefore} → {mission.result.scoreAfter}
                </span>
                <span
                  className="glass-pill"
                  style={{
                    fontSize: 10.5,
                    padding: '2px 8px',
                    color: mission.result.fidelity.bannedHits.length === 0 ? C.green : C.orangeWarm,
                  }}
                >
                  fidelity {mission.result.fidelity.score}
                  {mission.result.fidelity.bannedHits.length > 0 &&
                    ` · banned: ${mission.result.fidelity.bannedHits.join(', ')}`}
                </span>
                <span
                  className="glass-pill"
                  style={{
                    fontSize: 10.5,
                    padding: '2px 8px',
                    color: mission.result.contractMet ? C.green : C.orangeWarm,
                  }}
                >
                  contract {mission.result.contractMet ? 'met' : 'not met'}
                </span>
              </div>

              <div style={{ display: 'grid', gap: 2 }}>
                {mission.result.contractReport.map((line) => (
                  <span
                    key={line}
                    className="t-meta"
                    style={{ fontSize: 11, color: line.startsWith('✗') ? C.orangeWarm : C.textMuted }}
                  >
                    {line}
                  </span>
                ))}
              </div>

              <pre
                style={{
                  margin: 0,
                  padding: 10,
                  maxHeight: 220,
                  overflow: 'auto',
                  fontSize: 11,
                  lineHeight: 1.5,
                  background: 'rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 10,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {mission.result.diff.lines.map((l, i) => (
                  <div
                    key={`${i}-${l.kind}`}
                    style={{
                      color: l.kind === 'add' ? C.green : l.kind === 'del' ? C.orangeWarm : C.textMuted,
                    }}
                  >
                    {l.kind === 'add' ? '+ ' : l.kind === 'del' ? '- ' : '  '}
                    {l.line}
                  </div>
                ))}
              </pre>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className="t-meta" style={{ fontSize: 10.5, color: C.textDim }}>
                  {mission.result.diff.summary} · prompt {mission.result.provenance.promptHash}
                </span>
                <button
                  className="glass-btn glass-btn--primary"
                  onClick={handleApproveMission}
                  disabled={!mission.result.contractMet}
                  title={
                    mission.result.contractMet
                      ? 'Apply the reviewed diff — reversible via Undo'
                      : 'Blocked: the outcome contract / fidelity gate is not met'
                  }
                  style={{
                    marginLeft: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    fontSize: 12,
                    padding: '5px 12px',
                    opacity: mission.result.contractMet ? 1 : 0.45,
                    cursor: mission.result.contractMet ? 'pointer' : 'default',
                  }}
                >
                  <Check size={12} /> Approve & apply (reversible)
                </button>
                <button
                  className="glass-btn"
                  onClick={handleRejectMission}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '5px 12px', cursor: 'pointer' }}
                >
                  <X size={12} /> Dismiss
                </button>
              </div>
            </GlassPanel>
          )}
        </div>

        {/* ---------------- Live panel (right) ---------------- */}
        <div style={{ display: 'grid', gap: 12 }}>
          {/* Dimension breakdown */}
          <div style={{ display: 'grid', gap: 6 }}>
            {vm.dimensions.map((d) => (
              <div key={d.key} style={{ display: 'grid', gap: 3 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    aria-hidden
                    style={{ width: 7, height: 7, borderRadius: 999, background: stateColor[d.state], flexShrink: 0 }}
                  />
                  <span className="t-meta" style={{ color: C.textMuted }}>{d.label}</span>
                  <span className="t-meta" style={{ marginLeft: 'auto', color: stateColor[d.state], fontVariantNumeric: 'tabular-nums' }}>
                    {d.score}/{d.max}
                  </span>
                </div>
                <div style={{ height: 3, borderRadius: 999, background: 'rgba(255,255,255,0.08)' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${d.score}%`,
                      borderRadius: 999,
                      background: stateColor[d.state],
                      transition: 'width 120ms ease',
                    }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Keyword chips */}
          {vm.termChips.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {vm.termChips.map((c) => (
                <span
                  key={c.term}
                  className="glass-pill"
                  title={`Target ${c.target} — currently ${c.count}×`}
                  style={{
                    fontSize: 11,
                    padding: '3px 10px',
                    color: stateColor[c.state],
                    background: `${stateColor[c.state]}1c`,
                    border: `1px solid ${stateColor[c.state]}44`,
                  }}
                >
                  {c.term} {c.count}× <span style={{ opacity: 0.7 }}>/ {c.target}</span>
                </span>
              ))}
            </div>
          )}

          {/* Fix cards — every one a real applyFix dispatch. */}
          {vm.fixCards.length > 0 && (
            <div style={{ display: 'grid', gap: 6 }}>
              <span className="t-meta" style={{ color: C.textMuted, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Wrench size={12} /> Prioritized fixes
              </span>
              {vm.fixCards.slice(0, 6).map((f) => (
                <button
                  key={f.id}
                  className="glass-btn"
                  onClick={() => handleFix(f.id)}
                  disabled={!f.enabled}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    textAlign: 'left',
                    padding: '7px 10px',
                    fontSize: 12,
                    color: C.text,
                    cursor: f.enabled ? 'pointer' : 'default',
                    opacity: f.enabled ? 1 : 0.5,
                  }}
                >
                  <Sparkles size={12} style={{ color: C.magentaSoft, flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0 }}>{f.label}</span>
                  <span className="t-meta" style={{ color: C.green, flexShrink: 0 }}>
                    +{f.expectedGain}
                  </span>
                </button>
              ))}
            </div>
          )}
          {hasContent && vm.fixCards.length === 0 && (
            <span className="t-meta" style={{ color: C.green }}>
              No outstanding fixes — every dimension is clean.
            </span>
          )}

          {/* SERP snippet preview */}
          <Collapsible icon={<Search size={13} style={{ color: C.blueLight }} />} title="SERP preview">
            <div style={{ display: 'grid', gap: 3 }}>
              <span className="t-meta" style={{ color: C.textDim, fontSize: 11 }}>{serp.urlDisplay}</span>
              <span style={{ color: C.blueLight, fontSize: 16, lineHeight: 1.3 }}>
                {serp.titleDisplay || <em style={{ color: C.textDim }}>Untitled page</em>}
              </span>
              <span className="t-meta" style={{ color: C.textMuted, fontSize: 12.5, lineHeight: 1.5 }}>
                {serp.metaDisplay || <em style={{ color: C.textDim }}>No meta description yet.</em>}
              </span>
              {(serp.titleTruncated || serp.metaTruncated) && (
                <span className="t-meta" style={{ color: C.orange, fontSize: 11 }}>
                  {serp.titleTruncated ? 'Title truncates. ' : ''}
                  {serp.metaTruncated ? 'Meta truncates.' : ''}
                </span>
              )}
            </div>
          </Collapsible>

          {/* AEO snippet + JSON-LD */}
          <Collapsible icon={<Sparkles size={13} style={{ color: C.magentaSoft }} />} title="AEO preview · JSON-LD">
            {aeo.answerBlock ? (
              <div style={{ display: 'grid', gap: 4 }}>
                <span className="t-meta" style={{ color: C.textMuted }}>
                  Extractable answer ({aeo.faqPairs.length} Q&A pair{aeo.faqPairs.length === 1 ? '' : 's'})
                </span>
                <blockquote
                  style={{
                    margin: 0,
                    padding: '8px 12px',
                    borderLeft: `2px solid ${C.magentaSoft}`,
                    color: C.text,
                    fontSize: 12.5,
                    lineHeight: 1.55,
                    background: 'rgba(255,255,255,0.04)',
                    borderRadius: '0 10px 10px 0',
                  }}
                >
                  {aeo.answerBlock}
                </blockquote>
              </div>
            ) : (
              <span className="t-meta" style={{ color: C.textDim }}>
                No extractable answer yet — add a question-shaped H2 with a ≤60-word direct answer under it.
              </span>
            )}
            <JsonLdBlock label="FAQPage JSON-LD" json={aeo.faqJsonLd} />
            <JsonLdBlock label="Article JSON-LD" json={aeo.articleJsonLd} />
          </Collapsible>
        </div>
      </div>
    </GlassPanel>
  );
}
